import { useState, useEffect, useRef } from 'react';
import { X, Tablet, ChevronRight } from 'lucide-react';
import {
  banepadResolveCourt,
  courtDisplayName,
  banepadHourlyStatus,
  banepadHourEvents,
  banepadTypeTag,
  banepadBookingTag,
  banepadFormatLabel,
  banepadScoreText,
  banepadMatchClock,
  banepadBookingRange,
  CLUB_FORMATS,
  AMERICANO_FORMATS,
} from '../../engine';
import { PORTAL_BRAND } from '../../data/brand';
import { CourtPadTab } from './CourtPadTab';
import { KMSingleCourtPad } from './KMSingleCourtPad';
import { ClubAmericanoSingleCourtPad } from './ClubAmericanoSingleCourtPad';
import { AmericanoSingleCourtPad } from './AmericanoSingleCourtPad';
import { SeriespillSingleCourtPad } from './SeriespillSingleCourtPad';
import { SingleMatchSingleCourtPad } from './SingleMatchSingleCourtPad';
import { deviceFillBoxStyle } from './deviceFill';

// deviation: STARPOINT_ICON_WHITE_URI / STARPOINT_LOGO_WHITE_URI were defined in
// the original artifact (legacy/App.jsx); copied verbatim as module-local consts
// (public asset paths, not logic) so this component is self-contained. Mirrors the
// precedent in src/components/banepad/CourtPadCard.tsx.
const STARPOINT_ICON_WHITE_URI = '/starpoint-icon-white.png';
const STARPOINT_LOGO_WHITE_URI = '/starpoint-logo-white.png';

export interface BanepadCourtScreenProps {
  /** The court this single screen represents. */
  court: any;
  /** Per-court metadata map (display names, sponsor logo/name). */
  courtMeta?: any;
  /** All bookings (drives the booking/idle resolution + day timeline). */
  bookings?: any[];
  /** The active tournament whose matches may run on this court. */
  tournament?: any;
  /** The active seriespill (league), if any. */
  activeSeries?: any;
  /** Activity types (drives booking tags). */
  activities?: any[];
  /** Local planned (booker-picked) single matches — drives the enkeltmatch pad. */
  plannedMatches?: any[];
  /** Live `Date` ticked by the parent. */
  now: any;
  /** Closes/exits this screen (single-court mode). */
  onExit?: any;
  /** Opens the full tournament view for the resolved format. */
  onOpenTournamentView?: any;
  /** Opens the full seriespill view. */
  onOpenSeriesView?: any;
  /** Render in preview mode (no exit control, used in the central stacked preview). */
  preview?: boolean;
  /** Set of seriesIds forced live onto the pads. */
  forcedSessions?: any;
  /** Force the tournament onto this pad regardless of date/time. */
  forceTournament?: boolean;
  /** Force the seriespill onto this pad regardless of date/time. */
  forceSeries?: boolean;
  /** Force a single-match (enkeltmatch) activity onto its linked court's pad. */
  forceSingleMatch?: any;
  /** Records a Padel24 result (matchId, s1, s2, source). */
  onRecordResult?: any;
  /** Called when a player chip is clicked. */
  onPlayerClick?: any;
  /** Records a KM (club championship) result (updated match). */
  onRecordKMResult?: any;
  /** Persists a KM in-progress (live) score without finalising (updated match). */
  onRecordKMLiveScore?: any;
  /** Persists a Padel24/Americano in-progress score without finalising (matchId, {score1,score2}). */
  onRecordPadLiveScore?: any;
  /** Records a seriespill result (updated match). */
  onRecordSerieResult?: any;
  /** Persists a seriespill in-progress (live) score without finalising (updated match). */
  onRecordSerieLiveScore?: any;
  /** Records a single-match (enkeltmatch) result: (activity, { team1, team2, sets, playedAt }). */
  onRecordSingleMatchResult?: any;
  /**
   * What the pad's own result submission is doing, and for which match (#216).
   *
   * Threaded to the control the operator pressed rather than shown only in a
   * banner: «My eyes focused on the submit button after clicking it. It is
   * human nature.» Absent (Sentralen's preview, isolated renders) ⇒ the
   * buttons behave exactly as before.
   */
  submitStatus?: { phase: 'idle' | 'sending' | 'done'; matchId: string | null };
  /** Persists the live (in-progress) single-match score: (activity, liveScore). */
  onLiveSingleMatchScore?: any;
  /** All players (resolves names/ELO in the rendered pads). */
  allPlayers?: any[];
  /**
   * Adapts an Americano match shape into the slot-based shape CourtPadTab expects.
   * Helper passed in by the caller; forwarded to the Americano/Club single-court pads.
   */
  americanoMatchToSlots: (m: any) => any;
  /**
   * The live `Padel24Engine` wiring object. Forwarded to the Padel24 <CourtPadTab>
   * and the Americano/Club pads (→ <ClubPadBanner>).
   * (deviation: the original artifact read this via closure.)
   */
  Padel24Engine: any;
  /**
   * Plays an end-sound by key — shared audio helper (Web Audio). Forwarded to the
   * Padel24 + Americano/Club pads.
   * (deviation: the original artifact read this via closure.)
   */
  playEndSound: (soundType?: string) => void;
  /** Device kiosk mode: let the result pads fill the (screen-fitted) container (drop their size caps). */
  fill?: boolean;
  /**
   * Facts computed by the SERVER, for a pad that holds only its own court.
   *
   * Each of these genuinely depends on data beyond this court (#328): «Kamp 1»
   * is the first match played anywhere that day, the club scoreboard totals
   * every court, and a seriespill table spans its whole division. A pad fed by
   * `/pad/state` receives them already computed and never receives the matches
   * behind them. Omitted → every renderer computes its own, exactly as before.
   */
  projected?: {
    globalMatchNumber?: Record<string, number> | null;
    clubStandings?: { standings: any; leader: any } | null;
    serieStandings?: any[] | null;
  } | null;
  /**
   * Which heading level the court name is (#781).
   *
   * The pad had NO heading at all — the court name, which is the whole subject
   * of the screen, was a styled <span> — so there was nothing for a screen
   * reader's heading list to offer. On the physical pad this component IS the
   * page, so the default is `1`. The two places that embed it in a portal view
   * pass `2`: Sentralen stacks one pad per court, and a page with six `h1`s is
   * a worse outline than no `h1` at all.
   */
  headingLevel?: 1 | 2;
}

/**
 * One Banepad "iPad screen" for a single court — resolves the court's live mode (tournament /
 * booking / idle) and renders the matching pad: the full Padel24 <CourtPadTab>, the KM / Club /
 * Americano / seriespill single-court pads for live result entry, or a status frame (booking /
 * idle) with a scrollable 24h timeline quick-bar and sponsor footer. Extracted verbatim from the
 * original artifact (legacy/App.jsx); markup/logic unchanged. Takes `americanoMatchToSlots`,
 * `Padel24Engine` and `playEndSound` as props.
 */
export function BanepadCourtScreen({
  court,
  courtMeta = {},
  bookings = [],
  tournament = null,
  activeSeries = null,
  activities = [],
  plannedMatches = [],
  now,
  onExit = null,
  onOpenTournamentView = null,
  onOpenSeriesView = null,
  preview = false,
  forcedSessions = null,
  forceTournament = false,
  forceSeries = false,
  forceSingleMatch = null,
  onRecordResult = null,
  onPlayerClick = null,
  onRecordKMResult = null,
  onRecordKMLiveScore = null,
  onRecordPadLiveScore = null,
  onRecordSerieResult = null,
  onRecordSerieLiveScore = null,
  onRecordSingleMatchResult = null,
  submitStatus,
  onLiveSingleMatchScore = null,
  allPlayers = [],
  americanoMatchToSlots,
  Padel24Engine,
  playEndSound,
  fill = false,
  projected = null,
  headingLevel = 1,
}: BanepadCourtScreenProps) {
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const clock = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const r = banepadResolveCourt({
    court,
    bookings,
    tournament,
    activeSeries,
    activities,
    plannedMatches,
    nowMin,
    todayISO,
    forcedSessions,
    forceTournament,
    forceSeries,
    forceSingleMatch,
  });
  const name = courtDisplayName(court, courtMeta);
  // The court name is a HEADING now, not a styled span. Tailwind's preflight
  // resets `h1`/`h2` margins and font to inherit, so the element swap is
  // invisible: the kiosk's `cqw` type scale and layout are untouched.
  const CourtHeading = headingLevel === 1 ? 'h1' : 'h2';
  const meta = courtMeta?.[court] || {};
  const sponsorLogo = meta.sponsorLogo;
  const sponsorName = meta.sponsorName;
  const hourly = banepadHourlyStatus(
    court,
    bookings,
    tournament,
    activeSeries,
    nowMin,
    todayISO,
    24,
    0,
  );

  // Interactive: click an hour in the quick-bar → show the event(s) in that hour.
  // Click the same hour again / outside / 15s idle → back to the default view.
  const [selHour, setSelHour] = useState(null);
  useEffect(() => {
    if (selHour == null) return undefined;
    const id = setTimeout(() => setSelHour(null), 15000);
    return () => clearTimeout(id);
  }, [selHour]);
  const pickHour = (e, hourAbs) => {
    e.stopPropagation();
    setSelHour((prev) => (prev === hourAbs ? null : hourAbs));
  };
  const hourEvents =
    selHour != null
      ? banepadHourEvents(court, bookings, tournament, activeSeries, selHour, todayISO)
      : null;

  // Scrollable timeline (touch): the whole day, scroll up for history. Hidden
  // scrollbar. 15s without interaction → scroll back to the present.
  const railRef = useRef(null);
  const scrollTimer = useRef(null);
  const suppressScroll = useRef(false);
  const userScrolled = useRef(false);
  const scrollToNow = () => {
    const el = railRef.current;
    if (!el) return;
    const nowEl = el.querySelector('[data-now="1"]');
    suppressScroll.current = true;
    if (nowEl) {
      const top = nowEl.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
      el.scrollTop = Math.max(0, top - 2); // current hour at the very top
    } else el.scrollTop = 0;
    userScrolled.current = false;
    setTimeout(() => {
      suppressScroll.current = false;
    }, 80);
  };
  // deviation (lint): the original artifact has a bare `// eslint-disable-line` here (mount-only
  // scroll-to-now; scrollToNow intentionally omitted from deps). This project's config
  // does not flag it, so the directive would be an "unused eslint-disable" error —
  // dropped. Behaviour (empty dep array, runs once on mount) is unchanged.
  useEffect(() => {
    scrollToNow();
  }, []);
  const curHour = Math.floor(nowMin / 60);
  useEffect(() => {
    if (!userScrolled.current) scrollToNow();
  }, [curHour]); // follow the hour change when the user has not scrolled
  const onRailScroll = () => {
    if (suppressScroll.current) return;
    userScrolled.current = true;
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => scrollToNow(), 15000);
  };

  // Color codes for the quick-bar. The past (history) is shown in lighter/muted colors,
  // but free vs booked is still a visible difference (green vs orange).
  const railCol = { idle: '#22c55e', booking: '#f97316', tournament: '#e6c668' };
  const railBg = {
    idle: 'rgba(34,197,94,0.14)',
    booking: 'rgba(249,115,22,0.14)',
    tournament: 'rgba(230,198,104,0.14)',
  };
  const railColPast = {
    idle: 'rgba(134,239,172,0.55)',
    booking: 'rgba(253,186,116,0.55)',
    tournament: 'rgba(253,230,138,0.55)',
  };
  const railBgPast = {
    idle: 'rgba(134,239,172,0.06)',
    booking: 'rgba(253,186,116,0.06)',
    tournament: 'rgba(253,230,138,0.06)',
  };
  const railTxt = { idle: 'Ledig', booking: 'Booket', tournament: 'Turn.' };

  // Activity tag as a colored pill (scales with the pad width), placed on the right.
  const typeTagEl = (tag, { fontSize = '1.6cqw', padding = '0.4cqw 1.1cqw' } = {}) =>
    tag ? (
      <span
        className="font-mono font-bold uppercase tracking-wider rounded flex-shrink-0"
        style={{
          fontSize,
          padding,
          background: tag.color + '22',
          color: tag.color,
          border: `1px solid ${tag.color}55`,
          whiteSpace: 'nowrap',
        }}
      >
        {tag.label}
      </span>
    ) : null;

  // Content when an hour is selected: the event(s) in that hour (both on a match changeover).
  const renderHourDetail = () => {
    const hh = String(selHour % 24).padStart(2, '0');
    const next = String((selHour + 1) % 24).padStart(2, '0');
    const evs = hourEvents || [];
    const changeover = evs.length > 1;
    return (
      <div className="flex-1 flex flex-col justify-center" style={{ minHeight: 0 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: '1.4cqw' }}>
          <div
            className="font-mono uppercase tracking-[0.2em]"
            style={{ fontSize: '2.1cqw', color: '#e6c668' }}
          >
            Kl {hh}–{next}
          </div>
          {changeover && (
            <div
              className="font-mono uppercase tracking-wider rounded-full"
              style={{
                fontSize: '1.7cqw',
                padding: '0.5cqw 1.4cqw',
                background: 'rgba(230,198,104,0.16)',
                color: '#e6c668',
              }}
            >
              Kampskifte
            </div>
          )}
        </div>
        {evs.length === 0 ? (
          <div
            className="rounded-xl flex flex-col items-center justify-center"
            style={{
              background: 'rgba(34,197,94,0.10)',
              borderLeft: '0.6cqw solid #22c55e',
              padding: '3cqw',
            }}
          >
            <div
              className="font-display font-bold"
              style={{ fontSize: '3.4cqw', color: '#6ee7b7' }}
            >
              Ledig hele timen
            </div>
          </div>
        ) : (
          <div className="flex flex-col" style={{ gap: '1.4cqw' }}>
            {evs.map((ev, i) => {
              const evTag =
                ev.kind === 'tournament'
                  ? banepadTypeTag(ev.formatType)
                  : banepadBookingTag(ev, activities, activeSeries, plannedMatches);
              return (
                <div
                  key={i}
                  className="rounded-xl"
                  style={{
                    background: railBg[ev.kind],
                    borderLeft: `0.6cqw solid ${railCol[ev.kind]}`,
                    padding: '1.8cqw 2.4cqw',
                  }}
                >
                  <div className="flex items-center justify-between" style={{ gap: '2cqw' }}>
                    <div
                      className="font-mono uppercase tracking-wider"
                      style={{ fontSize: '1.8cqw', color: railCol[ev.kind] }}
                    >
                      {ev.kind === 'tournament' ? banepadFormatLabel(ev.formatType) : 'Booket'} ·{' '}
                      <span className="tabular-nums">{ev.range}</span>
                    </div>
                    <div className="flex items-center" style={{ gap: '1.4cqw' }}>
                      {banepadScoreText(ev.score) && (
                        <div
                          className="font-mono font-bold tabular-nums"
                          style={{ fontSize: '3cqw', color: '#e6c668' }}
                        >
                          {banepadScoreText(ev.score)}
                        </div>
                      )}
                      {typeTagEl(evTag)}
                    </div>
                  </div>
                  <div
                    className="font-display font-bold truncate"
                    style={{ fontSize: '3.6cqw', marginTop: '0.4cqw' }}
                  >
                    {ev.name}
                  </div>
                  {ev.notes && (
                    <div
                      style={{
                        fontSize: '2cqw',
                        color: 'rgba(230,251,255,0.7)',
                        marginTop: '0.4cqw',
                      }}
                    >
                      {ev.notes}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div
          className="font-mono uppercase tracking-wider"
          style={{ fontSize: '1.5cqw', color: 'rgba(230,251,255,0.35)', marginTop: '1.6cqw' }}
        >
          Trykk timen igjen eller vent for å gå tilbake
        </div>
      </div>
    );
  };

  // Shared frame. Everything scales with the pad width via container-query units (cqw),
  // so the pad looks equally proportional at full iPad size as in the preview.
  const renderFrame = ({
    children,
    badge,
    badgeBg,
    badgeColor,
    topStripe = null,
    centerLogo = false,
  }) => {
    // Readable text color (black/white) on top of an arbitrary background color.
    const txtOn = (hex) => {
      const h = String(hex || '').replace('#', '');
      if (h.length < 6) return '#ffffff';
      const r = parseInt(h.slice(0, 2), 16),
        g = parseInt(h.slice(2, 4), 16),
        b = parseInt(h.slice(4, 6), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#0a0b10' : '#ffffff';
    };
    // A tap anywhere on the frame drops the picked hour. That only repeats the
    // hour's own toggle button (`aria-pressed`, a second press un-picks it) and
    // the 15 s timeout, so the frame is role="presentation", not a control (#900).
    return (
      <div
        role="presentation"
        onClick={() => selHour != null && setSelHour(null)}
        className="rounded-2xl overflow-hidden flex flex-col"
        style={{
          aspectRatio: '4 / 3',
          containerType: 'inline-size',
          background: 'linear-gradient(165deg, #0a0b10 0%, #14161f 100%)',
          color: '#e6fbff',
          ...deviceFillBoxStyle(fill),
        }}
      >
        {/* Header */}
        {centerLogo ? (
          <div
            className="grid items-center"
            style={{
              gridTemplateColumns: '1fr auto 1fr',
              padding: '2.4cqw 3cqw 1.6cqw',
              gap: '1.6cqw',
            }}
          >
            <CourtHeading
              className="font-display font-bold truncate"
              style={{ fontSize: '3.6cqw', lineHeight: 1, justifySelf: 'start' }}
            >
              {name}
            </CourtHeading>
            <img
              src={STARPOINT_LOGO_WHITE_URI}
              alt="Starpoint"
              style={{
                height: '5.4cqw',
                width: 'auto',
                objectFit: 'contain',
                justifySelf: 'center',
              }}
            />
            <div
              className="flex items-center flex-shrink-0"
              style={{ gap: '1.6cqw', justifySelf: 'end' }}
            >
              {badge && (
                <span
                  className="font-mono font-bold uppercase tracking-wider rounded-full"
                  style={{
                    fontSize: '2.1cqw',
                    padding: '0.7cqw 1.6cqw',
                    background: badgeBg,
                    color: badgeColor,
                  }}
                >
                  {badge}
                </span>
              )}
              <span
                className="font-mono tabular-nums"
                style={{ fontSize: '4.4cqw', fontWeight: 700, color: '#e6c668' }}
              >
                {clock}
              </span>
            </div>
          </div>
        ) : (
          <div
            className="flex items-center justify-between"
            style={{ padding: '2.6cqw 3cqw 1.6cqw' }}
          >
            <div className="flex items-center min-w-0" style={{ gap: '1.6cqw' }}>
              <img
                src={STARPOINT_ICON_WHITE_URI}
                alt=""
                style={{ height: '6.6cqw', width: 'auto', objectFit: 'contain' }}
                className="flex-shrink-0"
              />
              <CourtHeading
                className="font-display font-bold truncate"
                style={{ fontSize: '5cqw', lineHeight: 1 }}
              >
                {name}
              </CourtHeading>
            </div>
            <div className="flex items-center flex-shrink-0" style={{ gap: '1.6cqw' }}>
              {badge && (
                <span
                  className="font-mono font-bold uppercase tracking-wider rounded-full"
                  style={{
                    fontSize: '2.1cqw',
                    padding: '0.7cqw 1.6cqw',
                    background: badgeBg,
                    color: badgeColor,
                  }}
                >
                  {badge}
                </span>
              )}
              <span
                className="font-mono tabular-nums"
                style={{ fontSize: '5cqw', fontWeight: 700, color: '#e6c668' }}
              >
                {clock}
              </span>
            </div>
          </div>
        )}
        {/* Tournament-type stripe — between the Starpoint logo and the matches (P24/KM/seriespill) */}
        {topStripe && (
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ background: topStripe.color, padding: '1.3cqw 3cqw' }}
          >
            <span
              className="font-mono font-bold uppercase"
              style={{ fontSize: '2.6cqw', letterSpacing: '0.3em', color: txtOn(topStripe.color) }}
            >
              {topStripe.label}
            </span>
          </div>
        )}
        {/* Main area + right-hand quick-bar */}
        <div className="flex-1 flex" style={{ minHeight: 0 }}>
          <div
            className="flex-1 flex flex-col"
            style={{ minHeight: 0, overflow: 'hidden', padding: '0 0 2cqw 3cqw' }}
          >
            {selHour != null ? renderHourDetail() : children}
          </div>
          {/* Quick bar — scrollable timeline for the whole day (touch), hidden scrollbar */}
          <div
            className="flex flex-col flex-shrink-0"
            style={{ width: '17cqw', padding: '0 2.6cqw 2cqw 1.6cqw', minHeight: 0 }}
          >
            <div
              className="font-mono uppercase text-center flex-shrink-0"
              style={{
                fontSize: '1.5cqw',
                letterSpacing: '0.14em',
                color: 'rgba(230,251,255,0.4)',
                marginBottom: '0.6cqw',
              }}
            >
              I dag
            </div>
            <div
              ref={railRef}
              onScroll={onRailScroll}
              className="bp-rail flex flex-col"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                gap: '0.7cqw',
              }}
            >
              <style>{'.bp-rail::-webkit-scrollbar{display:none}'}</style>
              {hourly.map((h, i) => {
                const isSel = selHour === h.hourAbs;
                const col = h.isPast ? railColPast : railCol;
                const bg = h.isPast ? railBgPast : railBg;
                return (
                  /* A REAL BUTTON. It announced itself as one and took focus,
                     but carried only `onClick` — so Enter and Space did
                     nothing and the whole day's timeline was unusable from a
                     keyboard (WCAG 2.1.1). `aria-pressed` carries the
                     selection that the gold outline shows. `text-left`
                     because a <button> centres its text by default and this
                     one must not. */
                  <button
                    key={i}
                    type="button"
                    data-now={h.isNow ? '1' : undefined}
                    aria-pressed={isSel}
                    onClick={(e) => pickHour(e, h.hourAbs)}
                    /* ── SELECTION MOVED OFF `outline` (#816 review) ──────
                       The gold selected ring and the white current-hour ring
                       used inline `outline`, and for every ordinary hour —
                       most of the day — that meant inline `outline: 'none'`.
                       An inline style beats any stylesheet, so it defeated
                       both the browser's own focus ring AND the app's
                       `:focus-visible` one: the keyboard navigation this PR
                       adds was unlocatable on exactly the control it adds it
                       to.

                       `box-shadow` with a spread draws the same ring outside
                       the border box and affects layout no more than `outline`
                       did, so the kiosk looks identical and the outline
                       property is free for focus.

                       The focus ring is declared HERE rather than inherited,
                       because the global one is `var(--ink)` — near-black, and
                       invisible on this dark pad. White, at the same `cqw`
                       scale as everything else on the pad. */
                    className="flex items-stretch rounded cursor-pointer flex-shrink-0 text-left focus-visible:[outline:0.3cqw_solid_#ffffff] focus-visible:[outline-offset:0.15cqw]"
                    style={{
                      height: '8cqw',
                      gap: '0.9cqw',
                      padding: '0.3cqw',
                      boxShadow: isSel
                        ? '0 0 0 0.3cqw #e6c668'
                        : h.isNow
                          ? '0 0 0 0.25cqw rgba(255,255,255,0.5)'
                          : undefined,
                      opacity: h.isPast ? 0.92 : 1,
                    }}
                  >
                    <span
                      className="font-mono tabular-nums flex items-center flex-shrink-0"
                      style={{
                        fontSize: '2.4cqw',
                        fontWeight: h.isNow || isSel ? 700 : 500,
                        color: isSel
                          ? '#e6c668'
                          : h.isNow
                            ? '#fff'
                            : h.isPast
                              ? 'rgba(230,251,255,0.4)'
                              : 'rgba(230,251,255,0.7)',
                      }}
                    >
                      {h.label}
                    </span>
                    <div className="flex flex-col" style={{ flex: 1, minHeight: 0, gap: '0.3cqw' }}>
                      {h.segs.map((sg, k) => (
                        <div
                          key={k}
                          className="flex items-center rounded-sm overflow-hidden"
                          style={{
                            flexGrow: sg.mins,
                            flexBasis: 0,
                            minHeight: 0,
                            background: bg[sg.kind],
                            borderLeft: `0.55cqw solid ${col[sg.kind]}`,
                            paddingLeft: '0.9cqw',
                          }}
                        >
                          {sg.mins >= 25 && (
                            <span
                              className="font-mono uppercase truncate"
                              style={{
                                fontSize: '1.4cqw',
                                letterSpacing: '0.03em',
                                color: col[sg.kind],
                              }}
                            >
                              {railTxt[sg.kind]}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {/* Sponsor — prominent, only on sponsored courts */}
        {(sponsorLogo || sponsorName) && (
          <div
            className="flex items-center justify-center"
            style={{
              gap: '1.8cqw',
              padding: '1.4cqw 3cqw',
              borderTop: '1px solid rgba(255,255,255,0.10)',
              background: 'rgba(230,198,104,0.06)',
            }}
          >
            <span
              className="font-mono uppercase tracking-[0.2em]"
              style={{ fontSize: '1.7cqw', color: '#e6c668' }}
            >
              Banen er sponset av
            </span>
            {sponsorLogo && (
              <img
                src={sponsorLogo}
                alt=""
                style={{
                  height: '4cqw',
                  width: 'auto',
                  objectFit: 'contain',
                  background: '#fff',
                  borderRadius: 5,
                  padding: '0.4cqw 0.7cqw',
                }}
              />
            )}
            {sponsorName && (
              <span
                className="font-display font-bold"
                style={{ fontSize: '2.4cqw', color: '#ffffff' }}
              >
                {sponsorName}
              </span>
            )}
          </div>
        )}
        {/* Bottom control — only «Lukk» (no screen lock; each screen = one court) */}
        {!preview && onExit && (
          <div
            className="flex items-center justify-end"
            style={{ padding: '1.6cqw 3cqw', borderTop: '1px solid rgba(255,255,255,0.08)' }}
          >
            <button
              onClick={onExit}
              className="flex items-center gap-1.5 font-mono uppercase tracking-wider"
              style={{ fontSize: '1.8cqw', color: 'rgba(230,251,255,0.55)' }}
            >
              <X size={14} /> Lukk
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── TOURNAMENT/SERIES in progress or next on the court ──
  if (r.mode === 'tournament') {
    // Padel24: show the FULL court pad (CourtPadCard) with result entry instead
    // of a summary + button. Applies automatically when the tournament is live/
    // booked on the court, and on a manual force from the control panel.
    if (
      r.source === 'tournament' &&
      r.formatType === 'padel24' &&
      tournament &&
      Array.isArray(tournament.matches)
    ) {
      return (
        <div className="w-full">
          <CourtPadTab
            tournament={tournament}
            onlyCourt={court}
            now={now}
            globalMatchNumber={projected?.globalMatchNumber ?? null}
            clubStandings={projected?.clubStandings ?? null}
            fill={fill}
            onRecordResult={onRecordResult}
            submitStatus={submitStatus}
            onLiveScore={onRecordPadLiveScore}
            onPlayerClick={onPlayerClick}
            Padel24Engine={Padel24Engine}
            playEndSound={playEndSound}
          />
        </div>
      );
    }
    if (
      r.source === 'tournament' &&
      r.formatType === 'klubbmesterskap' &&
      tournament &&
      Array.isArray(tournament.matches)
    ) {
      return (
        <div className="w-full">
          <KMSingleCourtPad
            tournament={tournament}
            court={court}
            now={now}
            fill={fill}
            allPlayers={allPlayers}
            onRecordResult={onRecordKMResult}
            onLiveScore={onRecordKMLiveScore}
            onPlayerClick={onPlayerClick}
          />
        </div>
      );
    }
    if (
      r.source === 'tournament' &&
      CLUB_FORMATS.includes(r.formatType) &&
      tournament &&
      Array.isArray(tournament.matches)
    ) {
      return (
        <div className="w-full">
          <ClubAmericanoSingleCourtPad
            tournament={tournament}
            court={court}
            now={now}
            globalMatchNumber={projected?.globalMatchNumber ?? null}
            clubStandings={projected?.clubStandings ?? null}
            fill={fill}
            onRecordResult={onRecordResult}
            submitStatus={submitStatus}
            onLiveScore={onRecordPadLiveScore}
            onPlayerClick={onPlayerClick}
            americanoMatchToSlots={americanoMatchToSlots}
            Padel24Engine={Padel24Engine}
            playEndSound={playEndSound}
          />
        </div>
      );
    }
    if (
      r.source === 'tournament' &&
      AMERICANO_FORMATS.includes(r.formatType) &&
      tournament &&
      Array.isArray(tournament.matches)
    ) {
      return (
        <div className="w-full">
          <AmericanoSingleCourtPad
            tournament={tournament}
            court={court}
            now={now}
            globalMatchNumber={projected?.globalMatchNumber ?? null}
            clubStandings={projected?.clubStandings ?? null}
            fill={fill}
            onRecordResult={onRecordResult}
            submitStatus={submitStatus}
            onLiveScore={onRecordPadLiveScore}
            onPlayerClick={onPlayerClick}
            americanoMatchToSlots={americanoMatchToSlots}
            Padel24Engine={Padel24Engine}
            playEndSound={playEndSound}
          />
        </div>
      );
    }
    // Seriespill: full court pad in KM style with result entry (game/set).
    // The match rules (super-tiebreak/set) are read per match from match.matchFormat.
    if (r.source === 'series' && activeSeries && Array.isArray(activeSeries.matches)) {
      return (
        <div className="w-full">
          <SeriespillSingleCourtPad
            series={activeSeries}
            court={court}
            now={now}
            standings={projected?.serieStandings ?? null}
            fill={fill}
            allPlayers={allPlayers}
            onRecordResult={onRecordSerieResult}
            onLiveScore={onRecordSerieLiveScore}
            onPlayerClick={onPlayerClick}
          />
        </div>
      );
    }
    // Single match (enkeltmatch) run as an activity, linked to this court's booking.
    // Uses the NORMAL court-screen chrome (renderFrame: court header with number/
    // status/clock + the day rail), with the enkeltmatch module in the center where
    // a free court shows "Spiller nå", and the same "senere i dag" list below.
    if (r.source === 'single_match' && r.activity) {
      const emColor = '#0ea5e9';
      const played = r.activity?.status === 'completed';
      const smUpcoming = (r.next || []).slice(0, 3);
      return renderFrame({
        badge: played ? 'Spilt' : 'Enkeltkamp',
        badgeBg: played ? 'rgba(16,185,129,0.18)' : 'rgba(14,165,233,0.18)',
        badgeColor: played ? '#6ee7b7' : '#7dd3fc',
        topStripe: { label: 'Enkeltkamp', color: emColor },
        children: (
          <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
            <div
              className="flex-1 flex flex-col justify-center"
              style={{ minHeight: 0, paddingRight: '3cqw' }}
            >
              <SingleMatchSingleCourtPad
                activity={r.activity}
                allPlayers={allPlayers}
                onRecordResult={onRecordSingleMatchResult}
                // A result NAMES THE BOOKING for an enkeltmatch (see
                // submissionMatchId): `r.current` is that booking, and the
                // activity on screen is not what the command compares.
                submitPhase={
                  submitStatus && String(submitStatus.matchId ?? '') === String(r.current?.id ?? '')
                    ? submitStatus.phase
                    : 'idle'
                }
                onLiveScore={onLiveSingleMatchScore}
                onPlayerClick={onPlayerClick}
                accent={emColor}
              />
            </div>
            {smUpcoming.length > 0 && (
              <div
                className="rounded-xl flex-shrink-0"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  padding: '1.6cqw 2.4cqw',
                  marginBottom: '0.4cqw',
                  marginRight: '3cqw',
                }}
              >
                <div
                  className="font-mono uppercase tracking-wider"
                  style={{
                    fontSize: '1.6cqw',
                    color: 'rgba(230,251,255,0.5)',
                    marginBottom: '0.6cqw',
                  }}
                >
                  Senere i dag
                </div>
                <div className="flex flex-col" style={{ gap: '0.3cqw' }}>
                  {smUpcoming.map((nb, i) => (
                    <div
                      key={i}
                      className="flex items-center"
                      style={{ fontSize: '2.1cqw', gap: '1.6cqw' }}
                    >
                      <span
                        className="font-mono tabular-nums flex-shrink-0"
                        style={{ color: '#e6c668', minWidth: '15cqw' }}
                      >
                        {banepadBookingRange(nb)}
                      </span>
                      <span className="truncate flex-1" style={{ color: 'rgba(230,251,255,0.9)' }}>
                        {nb.bookedBy?.name || nb.title || 'Booking'}
                      </span>
                      {typeTagEl(banepadBookingTag(nb, activities, activeSeries, plannedMatches))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ),
      });
    }
    const fmtLabel = banepadFormatLabel(r.formatType);
    const m = r.current || r.next;
    const isLive = !!r.current;
    const openView = () => {
      if (r.source === 'series') onOpenSeriesView?.();
      else onOpenTournamentView?.(r.formatType);
    };
    const typeColor = banepadTypeTag(r.formatType)?.color || '#22d3ee';
    return renderFrame({
      badge: isLive ? 'Live' : 'Neste',
      badgeBg: isLive ? '#a3e635' : 'rgba(165,243,252,0.18)',
      badgeColor: isLive ? '#1a2e05' : '#e6c668',
      topStripe: { label: fmtLabel, color: typeColor },
      centerLogo: true,
      children: (
        <div className="flex-1 flex flex-col justify-center" style={{ minHeight: 0 }}>
          <div
            className="font-display font-bold leading-tight"
            style={{ fontSize: '4.4cqw', marginBottom: '2cqw' }}
          >
            {r.name}
          </div>
          <div
            className="rounded-xl"
            style={{ background: 'rgba(255,255,255,0.06)', padding: '2.4cqw 2.8cqw' }}
          >
            <div className="flex items-center justify-between flex-wrap" style={{ gap: '2cqw' }}>
              <div
                className="font-mono uppercase tracking-wider"
                style={{ fontSize: '2cqw', color: 'rgba(230,251,255,0.6)' }}
              >
                {isLive ? 'Pågår nå' : 'Neste kamp'}
                {banepadMatchClock(m) ? ` · kl ${banepadMatchClock(m)}` : ''}
              </div>
              {banepadScoreText(m?.score) && (
                <div
                  className="font-mono font-bold tabular-nums"
                  style={{ fontSize: '4.4cqw', color: '#e6c668' }}
                >
                  {banepadScoreText(m?.score)}
                </div>
              )}
            </div>
            <div
              style={{ fontSize: '2.2cqw', color: 'rgba(230,251,255,0.85)', marginTop: '1.2cqw' }}
            >
              Spillere og live resultat vises i {fmtLabel}-banepaden.
            </div>
          </div>
          <button
            onClick={openView}
            className="self-start flex items-center rounded-full font-semibold"
            style={{
              gap: '1.4cqw',
              marginTop: '2.4cqw',
              padding: '1.6cqw 3cqw',
              background: '#22d3ee',
              color: '#062c33',
              fontSize: '2.4cqw',
            }}
          >
            <Tablet size={16} /> Åpne {fmtLabel}-banepad <ChevronRight size={16} />
          </button>
        </div>
      ),
    });
  }

  // ── BOOKING in progress — time FIRST (from → to), then player ──
  if (r.mode === 'booking') {
    const b = r.current;
    const who = b?.bookedBy?.name || b?.title || 'Reservert';
    return renderFrame({
      badge: 'Booket',
      badgeBg: '#f97316',
      badgeColor: '#431407',
      children: (
        <>
          <div
            className="flex-1 flex flex-col justify-start"
            style={{ minHeight: 0, paddingTop: '5cqw' }}
          >
            {/* Playing now — moved down one line */}
            <div
              className="font-mono uppercase tracking-[0.2em]"
              style={{ fontSize: '2.1cqw', color: '#e6c668', marginBottom: '1cqw' }}
            >
              Spiller nå
            </div>
            {/* Time + SERIESPILL tag on the SAME line — the tag on the right, large */}
            <div className="flex items-end" style={{ gap: '3cqw' }}>
              <div>
                <div
                  className="font-mono uppercase"
                  style={{
                    fontSize: '1.7cqw',
                    letterSpacing: '0.12em',
                    color: 'rgba(230,251,255,0.5)',
                  }}
                >
                  Fra
                </div>
                <div
                  className="font-mono font-bold tabular-nums"
                  style={{ fontSize: '7cqw', lineHeight: 1, color: '#ffffff' }}
                >
                  {b?.startTime || '—'}
                </div>
              </div>
              <span
                className="font-mono"
                style={{
                  fontSize: '5cqw',
                  lineHeight: 1,
                  color: '#e6c668',
                  marginBottom: '0.4cqw',
                }}
              >
                →
              </span>
              <div>
                <div
                  className="font-mono uppercase"
                  style={{
                    fontSize: '1.7cqw',
                    letterSpacing: '0.12em',
                    color: 'rgba(230,251,255,0.5)',
                  }}
                >
                  Til
                </div>
                <div
                  className="font-mono font-bold tabular-nums"
                  style={{ fontSize: '7cqw', lineHeight: 1, color: '#ffffff' }}
                >
                  {b?.endTime || '—'}
                </div>
              </div>
              <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
                {typeTagEl(banepadBookingTag(b, activities, activeSeries, plannedMatches), {
                  fontSize: '3.2cqw',
                  padding: '1.2cqw 2.2cqw',
                })}
              </div>
            </div>
            {/* Title — wraps to up to 2 lines for long names */}
            <div style={{ marginTop: '2cqw' }}>
              <div
                className="font-display font-bold leading-tight"
                style={{
                  fontSize: '4.4cqw',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {who}
              </div>
            </div>
            {b?.notes && (
              <div
                style={{ fontSize: '2.2cqw', color: 'rgba(230,251,255,0.7)', marginTop: '0.6cqw' }}
              >
                {b.notes}
              </div>
            )}
            {r.prev && (
              <div
                className="font-mono uppercase tracking-wider"
                style={{ fontSize: '1.6cqw', color: 'rgba(230,251,255,0.4)', marginTop: '1.6cqw' }}
              >
                Forrige: {r.prev.bookedBy?.name || r.prev.title || 'Booking'} ·{' '}
                {banepadBookingRange(r.prev)}
              </div>
            )}
          </div>
          {r.next?.length > 0 && (
            <div
              className="rounded-xl flex-shrink-0"
              style={{
                background: 'rgba(255,255,255,0.06)',
                padding: '1.6cqw 2.4cqw',
                marginBottom: '0.4cqw',
              }}
            >
              <div
                className="font-mono uppercase tracking-wider"
                style={{
                  fontSize: '1.6cqw',
                  color: 'rgba(230,251,255,0.5)',
                  marginBottom: '0.6cqw',
                }}
              >
                Senere i dag
              </div>
              <div className="flex flex-col" style={{ gap: '0.3cqw' }}>
                {r.next.slice(0, 3).map((nb, i) => (
                  <div
                    key={i}
                    className="flex items-center"
                    style={{ fontSize: '2.1cqw', gap: '1.6cqw' }}
                  >
                    <span
                      className="font-mono tabular-nums flex-shrink-0"
                      style={{ color: '#e6c668', minWidth: '15cqw' }}
                    >
                      {banepadBookingRange(nb)}
                    </span>
                    <span className="truncate flex-1" style={{ color: 'rgba(230,251,255,0.9)' }}>
                      {nb.bookedBy?.name || nb.title || 'Booking'}
                    </span>
                    {typeTagEl(banepadBookingTag(nb, activities, activeSeries, plannedMatches))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ),
    });
  }

  // ── FREE ──
  const nextUp = r.next?.[0];
  const freeUntil = nextUp?.startTime;
  const nextList = (r.next || []).slice(0, 4);
  return renderFrame({
    badge: 'Ledig',
    badgeBg: 'rgba(16,185,129,0.18)',
    badgeColor: '#6ee7b7',
    children: (
      <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
        {/* Logo + status — at the top, compact, leaving room for more matches below */}
        <div
          className="flex flex-col items-center text-center flex-shrink-0"
          style={{ paddingTop: '4cqw' }}
        >
          <img
            src={STARPOINT_ICON_WHITE_URI}
            alt=""
            style={{ height: '8cqw', width: 'auto', objectFit: 'contain', marginBottom: '1cqw' }}
          />
          <div
            className="font-display font-bold"
            style={{ fontSize: '5cqw', lineHeight: 1, letterSpacing: '-0.01em' }}
          >
            <span style={{ color: '#e6c668' }}>{PORTAL_BRAND.wordmarkA}</span>
            <span style={{ color: '#ffffff' }}>{PORTAL_BRAND.wordmarkB}</span>
          </div>
          <div
            className="font-mono uppercase"
            style={{
              fontSize: '1.4cqw',
              letterSpacing: '0.36em',
              color: '#e6c668',
              marginTop: '0.8cqw',
              paddingLeft: '0.36em',
            }}
          >
            {PORTAL_BRAND.tagline}
          </div>
          <div
            className="font-display font-bold"
            style={{ fontSize: '2.6cqw', marginTop: '1.6cqw', color: 'rgba(244,244,246,0.88)' }}
          >
            {freeUntil ? `Banen er ledig frem til kl ${freeUntil}` : 'Banen er ledig'}
          </div>
        </div>
        {/* Upcoming matches — multiple, filling the rest of the space */}
        <div
          className="flex-1 flex flex-col justify-end"
          style={{ minHeight: 0, overflow: 'hidden', marginTop: '1.6cqw', gap: '0.8cqw' }}
        >
          {nextList.length > 0 ? (
            <>
              <div
                className="font-mono uppercase tracking-wider flex-shrink-0"
                style={{ fontSize: '1.5cqw', color: '#38bdf8' }}
              >
                Kommende
              </div>
              {nextList.map((nb, i) => (
                <div
                  key={i}
                  className="w-full rounded-lg flex items-center flex-shrink-0"
                  style={{
                    background: 'rgba(56,189,248,0.10)',
                    borderLeft: '0.5cqw solid #38bdf8',
                    padding: '1cqw 1.6cqw',
                    gap: '1.6cqw',
                  }}
                >
                  <span
                    className="font-mono tabular-nums flex-shrink-0"
                    style={{ fontSize: '2cqw', color: '#38bdf8', minWidth: '15cqw' }}
                  >
                    {banepadBookingRange(nb)}
                  </span>
                  <span
                    className="truncate flex-1"
                    style={{ fontSize: '2cqw', color: 'rgba(244,244,246,0.92)' }}
                  >
                    {nb.bookedBy?.name || nb.title || 'Booking'}
                  </span>
                  {typeTagEl(banepadBookingTag(nb, activities, activeSeries, plannedMatches))}
                </div>
              ))}
            </>
          ) : (
            <div
              className="w-full text-center"
              style={{ fontSize: '2cqw', color: 'rgba(244,244,246,0.45)' }}
            >
              Ingen flere bookinger i dag
            </div>
          )}
        </div>
      </div>
    ),
  });
}
